// pages/AICoding.js — Halaman AI Coding mandiri (full page).
// Dipisah dari AI Workspace: mode Coding TIDAK punya target dashboard, tidak
// lewat text-to-SQL, tidak lewat KB, dan tidak ikut auto-detect. Karena itu
// seluruh mesin picker/auto-detect di AIWorkspace.js tidak relevan di sini —
// halaman ini sengaja tipis: AIChat dikunci ke CODE_KEY, titik.
//
// Permission: 'ai_coding'. PENTING — permission ini BUKAN sekadar penjaga menu.
// Gerbang sesungguhnya ada di backend pada /api/ai/ask (tolak mode:'code' bila
// user tak punya 'ai_coding'), sebab mode ini membuka tool use di AI Service
// (read_file / write_file / pm2_logs). Menyembunyikan menu saja tidak cukup.
//
// Pemilih ROOT sengaja ditaruh DI SINI, bukan di AIChat.js: komponen itu
// dipakai bersama AI Workspace, panel AI header, dan AI Chat History — menaruh
// UI khusus Coding di sana berarti tiga halaman lain ikut menanggungnya.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FolderTree, Loader2, AlertTriangle, Upload, Trash2, Archive,
         FileText, Download, FolderDown, PanelLeftClose, PanelLeftOpen,
         Eye, X, Copy, Check } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { api } from '../context/AuthContext';
import { loadConfigFromServer, getDefaultConfig } from '../components/AI/aiConfig';
import AIChat, { CODE_KEY, getDashboardContext } from '../components/AI/AIChat';
import AIHistorySidebar from '../components/AI/AIHistorySidebar';
import useHistorySidebarSync from '../components/AI/useHistorySidebarSync';
import { toChatMessages } from '../components/AI/sessionResume';
import AIChangeReview from '../components/AI/AIChangeReview';
import useIsMobile from '../hooks/useIsMobile';

// Tampilkan '/dashboard' apa adanya — path pendek dan sudah jelas. Untuk path
// panjang, potong dari DEPAN supaya bagian ekornya (yang membedakan) tetap
// terbaca: '…/ec2-user/ai-drafts', bukan '/home/ec2-us…'.
function shortRoot(p) {
  const s = String(p || '');
  return s.length <= 28 ? s : '…' + s.slice(-27);
}

// String baru panel unggah SENGAJA tidak masuk translations.js. Berkas itu punya
// gerbang CI prebuild (check-i18n.js) yang menuntut paritas penuh id/en/ar, dan
// fallback `t('k') || 'default'` TIDAK menyelamatkan build. Panel ini admin-only
// dan sudah berbahasa Indonesia di sebagian besar pesan backend-nya, jadi tabel
// lokal lebih jujur daripada tiga terjemahan yang dua di antaranya tidak akan
// pernah dibaca siapa pun.
const L = {
  tidyLabel:  'Rapikan dengan AI',
  tidyHint:   'Hanya untuk PDF/Word yang hasil mentahnya berantakan. Mati secara bawaan: '
            + 'LLM yang merapikan bisa menghilangkan baris atau mengubah angka tanpa jejak.',
  convDone:   (n, from) => `${from.toUpperCase()} dikonversi → ${n}`,
  convRaw:    'teks mentah',
  convTidied: 'dirapikan LLM',
  convCut:    'DIPOTONG batas extractor',
  uploadHint: (docs, conv, mb, kb) =>
    `Teks (${docs}) disimpan apa adanya, maks ${kb} KB. `
    + `Dokumen (${conv}) dikonversi jadi .md, sumber maks ${mb} MB.`,
};

// reloadKey: dinaikkan oleh SandboxPanel setelah unggah/hapus.
//
// KOREKSI (komentar sebelumnya di sini SALAH): mengunggah sandbox TIDAK menambah
// entri baru di dropdown ini. AI_TOOLS_ROOTS di ai-service adalah IIFE atas env,
// dievaluasi sekali saat modul di-require, dan pickRoot() hanya menerima anggota
// persis daftar itu. Yang menjadi root adalah direktori INDUK ai-sandbox; hasil
// unggahan dibaca sebagai path relatif di bawahnya ('label/file.js', 'handoff.md').
//
// reloadKey dipertahankan karena `tools_enabled` / `write_root` di respons yang
// sama memang bisa berubah, dan biayanya satu permintaan ringan. Tapi jangan
// berharap daftar rootnya bertambah — kalau root baru memang dibutuhkan, itu
// perubahan env + `pm2 delete` → `pm2 start` di ai-service.
function RootPicker({ reloadKey = 0, onRoots }) {
  const { t } = useLang();
  const [roots, setRoots]       = useState(null);   // null = belum dimuat
  const [selected, setSelected] = useState('');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/ai/code-roots')
      .then(({ data }) => {
        if (!alive) return;
        setRoots(data?.roots || []);
        onRoots?.(data?.roots || []);
        // selected '' berarti "ikut default" — tampilkan default itu sebagai
        // yang aktif supaya user tidak melihat dropdown kosong dan mengira
        // belum ada root sama sekali.
        setSelected(data?.selected || data?.default || '');
      })
      .catch(e => { if (alive) setErr(e.response?.data?.error || e.message); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [reloadKey]);

  const onChange = useCallback(async (root) => {
    const prev = selected;
    setSelected(root);          // optimistik
    setSaving(true); setErr(null);
    try {
      await api.put('/api/ai/code-root', { root });
    } catch (e) {
      setSelected(prev);        // gagal → kembalikan, jangan tampilkan pilihan yang tak tersimpan
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  }, [selected]);

  // Tools mati / AI Service tak terjangkau → jangan tampilkan kontrol yang
  // tidak berpengaruh apa-apa.
  if (err && roots === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-amber-600">
        <AlertTriangle style={{ width: 13, height: 13 }} />
        {t('aiCoding.rootUnavailable')} — {err}
      </div>
    );
  }
  if (!roots || roots.length <= 1) return null;   // satu root saja → tak ada yang perlu dipilih

  return (
    <>
    {/* Padding, border, dan latar ada di PEMBUNGKUS di AICoding(), bukan di sini.
        Dulu ada di sini karena komponen ini satu-satunya penghuni bar itu;
        setelah SandboxPanel bergabung, mempertahankannya berarti padding dan
        border ganda. */}
    <div className="flex items-center gap-2 flex-wrap">
      <FolderTree style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" />
      <span className="text-xs text-slate-500 flex-shrink-0">
        {t('aiCoding.projectRoot')}
      </span>
      <select
        value={selected}
        onChange={e => onChange(e.target.value)}
        disabled={saving}
        className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white outline-none focus:border-brand-400 disabled:opacity-50"
      >
        {roots.map(r => <option key={r} value={r}>{shortRoot(r)}</option>)}
      </select>
      <EngineSelect />
      {saving && <Loader2 style={{ width: 12, height: 12 }} className="animate-spin text-slate-400" />}
      {err && <span className="text-xs text-rose-500 truncate">{err}</span>}
      <span className="text-[11px] text-slate-400 hidden sm:block">
        {t('aiCoding.rootHint')}
      </span>
    </div>
    </>
  );
}

// ── Panel sandbox zip ────────────────────────────────────────────────────────
// Ditaruh di halaman ini, bukan di AI Configuration: sandbox hanya berarti dalam
// konteks Coding, dan yang mengunggah biasanya sedang duduk di halaman ini
// hendak memeriksa kode. Alasannya sama dengan RootPicker di atas.
function SandboxPanel({ onChanged }) {
  const { t } = useLang();
  const [info, setInfo]   = useState(null);
  const [busy, setBusy]   = useState('');
  const [err, setErr]     = useState(null);
  const [msg, setMsg]     = useState(null);
  const [open, setOpen]   = useState(false);
  // MATI secara bawaan — lihat L.tidyHint. Hanya berpengaruh untuk pdf/docx;
  // backend mengabaikannya untuk xlsx dan untuk berkas teks.
  const [tidy, setTidy]   = useState(false);
  const fileRef = React.useRef(null);

  const load = useCallback(() => {
    api.get('/api/ai/sandbox')
      .then(({ data }) => setInfo(data))
      .catch(e => setErr(e.response?.data?.error || e.message));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = useCallback(async (file) => {
    if (!file) return;
    setBusy('upload'); setErr(null); setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    // Dikirim SELALU, bukan hanya saat aktif: FormData yang kehilangan field
    // membuat backend membaca undefined, dan cabang itu lebih sulit dilacak
    // daripada nilai 'false' yang eksplisit.
    fd.append('tidy', tidy ? 'true' : 'false');
    try {
      const { data } = await api.post('/api/ai/sandbox/upload', fd);
      if (data.kind === 'doc') {
        // Pesan dokumen menyebut NAMA berkasnya, bukan labelnya — nama itulah
        // yang harus diketik user ke dalam prompt agar model membacanya.
        //
        // Hasil KONVERSI diberi pesannya sendiri: namanya berubah (laporan.pdf →
        // laporan.md) dan mode ekstraksinya perlu terlihat. Tanpa itu user
        // mengetik `laporan.pdf` ke prompt dan model menjawab "tidak ditemukan".
        if (data.converted_from) {
          setMsg(`${L.convDone(data.name, data.converted_from)}`
            + ` · ${data.tidied ? L.convTidied : L.convRaw}`
            + (data.pages ? ` · ${data.pages} hal/sheet` : '')
            + (data.truncated ? ` · ${L.convCut}` : ''));
        } else {
          setMsg(t('aiCoding.sbx.docDone', { name: data.name }));
        }
      } else {
        const skipped = Object.entries(data.skipped || {})
          .map(([k, v]) => `${k}: ${v}`).join(', ');
        setMsg(t('aiCoding.sbx.done', { label: data.label, files: data.files })
          + (data.truncated ? ` — ${t('aiCoding.sbx.truncated')}` : '')
          + (skipped ? ` — ${t('aiCoding.sbx.skipped')} ${skipped}` : ''));
      }
      load();
      onChanged?.();          // dropdown root harus ikut segar
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';   // izinkan unggah ulang berkas yang sama
    }
    // `tidy` WAJIB ada di daftar ini. Tanpa itu callback membeku pada nilai saat
    // pertama dirender (false), dan centangnya jadi hiasan yang tidak pernah
    // berpengaruh — gagal yang sepenuhnya senyap.
  }, [t, load, onChanged, tidy]);

  const del = useCallback(async (label) => {
    setBusy(label); setErr(null); setMsg(null);
    try {
      await api.delete(`/api/ai/sandbox/${encodeURIComponent(label)}`);
      load(); onChanged?.();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  }, [load, onChanged]);

  const delDoc = useCallback(async (name) => {
    setBusy(`doc:${name}`); setErr(null); setMsg(null);
    try {
      await api.delete(`/api/ai/sandbox/doc/${encodeURIComponent(name)}`);
      load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  }, [load]);

  const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600 flex-shrink-0">
        <Archive style={{ width: 13, height: 13 }} />
        {t('aiCoding.sbx.open')}
      </button>
    );
  }

  return (
    <div className="w-full border-t border-slate-100 pt-2 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Archive style={{ width: 13, height: 13 }} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-600">{t('aiCoding.sbx.title')}</span>
        {/* Satu <input> untuk semua jenis. Backend yang membedakan berdasarkan
            ekstensi; memisah menjadi beberapa input hanya menggandakan state
            tanpa menambah kejelasan, karena tujuan akhirnya direktori yang sama.

            `accept` dirakit dari daftar yang DIKIRIM backend, bukan ditulis
            literal di sini. Dua daftar yang harus dijaga sinkron manual pasti
            menyimpang — dan menyimpangnya senyap: berkas yang sah tidak muncul
            di dialog pemilih, tanpa error apa pun. Fallback dipakai hanya
            sebelum /api/ai/sandbox terjawab. */}
        <input ref={fileRef} type="file" className="hidden"
          accept={['.zip', ...(info?.doc_ext || ['.md', '.txt']), ...(info?.conv_ext || [])].join(',')}
          onChange={e => upload(e.target.files?.[0])} />
        <button type="button" disabled={!!busy} onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white
                     hover:border-brand-400 disabled:opacity-50">
          {busy === 'upload'
            ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
            : <Upload style={{ width: 12, height: 12 }} />}
          {t('aiCoding.sbx.upload')}
        </button>
        <button type="button" onClick={() => setOpen(false)}
          className="text-xs text-slate-400 hover:text-slate-600 ml-auto">
          {t('aiCoding.sbx.close')}
        </button>
      </div>

      {/* Python belum dikonfigurasi = fitur mati total. Diberitahu di depan,
          bukan dibiarkan muncul sebagai error 500 setelah user memilih berkas. */}
      {info && !info.python_configured && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
          {t('aiCoding.sbx.noPython')}
        </div>
      )}
      {info && (
        <div className="text-[11px] text-slate-400 mt-1.5">
          {t('aiCoding.sbx.limits', { mb: info.max_mb, files: info.max_files, days: info.ttl_days })}
        </div>
      )}
      {info && (
        <div className="text-[11px] text-slate-400 mt-1">
          {t('aiCoding.sbx.docHint', { kb: info.doc_max_kb, root: info.root })}
        </div>
      )}
      {info?.conv_ext?.length > 0 && (
        <div className="text-[11px] text-slate-400 mt-1">
          {L.uploadHint((info.doc_ext || []).join(' '), (info.conv_ext || []).join(' '),
            info.conv_max_mb, info.doc_max_kb)}
        </div>
      )}
      {/* Centang hanya ditampilkan bila konversi memang tersedia — tanpa
          conv_ext ia tidak berpengaruh apa-apa, dan kontrol yang tidak
          berpengaruh lebih buruk daripada kontrol yang tidak ada. */}
      {info?.conv_ext?.length > 0 && (
        <label className="flex items-start gap-1.5 text-[11px] text-slate-500 mt-1.5 cursor-pointer">
          <input type="checkbox" checked={tidy} disabled={!!busy}
            onChange={e => setTidy(e.target.checked)}
            className="mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-medium text-slate-600">{L.tidyLabel}</span>
            {' — '}{L.tidyHint}
          </span>
        </label>
      )}
      {msg && <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200
                              rounded-md px-2 py-1.5 mt-2">{msg}</div>}
      {err && <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200
                              rounded-md px-2 py-1.5 mt-2">{err}</div>}

      {/* Dokumen instruksi ditampilkan LEBIH DULU dan dengan path lengkapnya
          apa adanya. Yang dibutuhkan user dari panel ini bukan konfirmasi bahwa
          unggahannya berhasil, tapi nama persis yang harus ia ketik ke prompt. */}
      {info?.docs?.length > 0 && (
        <div className="mt-2 space-y-1">
          {info.docs.map(d => (
            <div key={d.name} className="flex items-center gap-2 text-xs bg-white border border-slate-200
                                         rounded-lg px-2 py-1">
              <FileText style={{ width: 12, height: 12 }} className="text-brand-500 flex-shrink-0" />
              <span className="font-mono text-slate-700 truncate">{d.name}</span>
              <span className="text-slate-400 flex-shrink-0">{kb(d.bytes)}</span>
              <button type="button" disabled={busy === `doc:${d.name}`} onClick={() => delDoc(d.name)}
                title={t('aiCoding.sbx.docDelete')}
                className="ml-auto text-slate-400 hover:text-rose-600 disabled:opacity-50 flex-shrink-0">
                {busy === `doc:${d.name}`
                  ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                  : <Trash2 style={{ width: 12, height: 12 }} />}
              </button>
            </div>
          ))}
        </div>
      )}

      {info?.sandboxes?.length > 0 && (
        <div className="mt-2 space-y-1">
          {info.sandboxes.map(sb => (
            <div key={sb.label} className="flex items-center gap-2 text-xs bg-white border border-slate-200
                                           rounded-lg px-2 py-1">
              <span className="font-mono text-slate-700 truncate">{sb.label}</span>
              <span className="text-slate-400 flex-shrink-0">{sb.files} · {kb(sb.bytes)}</span>
              <button type="button" disabled={busy === sb.label} onClick={() => del(sb.label)}
                title={t('aiCoding.sbx.delete')}
                className="ml-auto text-slate-400 hover:text-rose-600 disabled:opacity-50 flex-shrink-0">
                {busy === sb.label
                  ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                  : <Trash2 style={{ width: 12, height: 12 }} />}
              </button>
            </div>
          ))}
        </div>
      )}
      {info && !info.sandboxes?.length && !info.docs?.length && (
        <div className="text-[11px] text-slate-400 mt-2">{t('aiCoding.sbx.empty')}</div>
      )}
    </div>
  );
}

// ── Panel draft AI Coding ─────────────────────────────────────────
// Isi direktori yang ditulis write_file. Ada di sini supaya hasil generate bisa
// diambil tanpa scp/WinSCP.
//
// Unduhan TIDAK memakai <a href> biasa: endpoint-nya di belakang authenticateToken
// dan token JWT hidup di interceptor axios, bukan di cookie. Navigasi browser
// biasa akan datang tanpa Authorization dan menerima 401. Karena itu berkas
// diambil sebagai blob lalu diserahkan ke browser lewat object URL.
/**
 * Pratinjau isi draft di dalam modal.
 *
 * TIDAK ADA ENDPOINT BARU. Ia memakai `/api/ai/drafts/file/:name` yang sama
 * dengan tombol unduh — blob yang sama, dibaca sebagai teks alih-alih
 * diserahkan ke browser. Konsekuensinya berguna: seluruh penjagaan di
 * `filePath()` (DENY_NAME_RE, ALLOWED_EXT, pengurungan ke ROOT) berlaku apa
 * adanya tanpa perlu dicerminkan ulang di sini. Berkas `downloadable: false`
 * juga tidak bisa dipratinjau, dan itu benar.
 *
 * ⚠ ISI WAJIB DIRENDER SEBAGAI TEKS, TIDAK PERNAH SEBAGAI HTML.
 * Backend memaksa `octet-stream` + `nosniff` (lihat aiDrafts.js:127-131) justru
 * karena draft bisa berisi `.html` atau `.js` yang DITULIS MODEL. Merendernya
 * dengan `dangerouslySetInnerHTML` — atau lewat MarkdownRenderer untuk berkas
 * `.md` — akan mengeksekusinya pada origin dashboard dan membatalkan seluruh
 * pertahanan itu. `<pre>{text}</pre>` di JSX meng-escape sendiri; jangan
 * diganti.
 */
const PREVIEW_MAX_BYTES = 512 * 1024;

function DraftPreviewModal({ name, onClose }) {
  const { t } = useLang();
  const [text, setText]   = useState(null);
  const [err, setErr]     = useState(null);
  const [trunc, setTrunc] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setText(null); setErr(null); setTrunc(false);
      try {
        const res = await api.get(`/api/ai/drafts/file/${encodeURIComponent(name)}`, { responseType: 'blob' });
        const blob = res.data;
        // Dipotong SEBELUM dibaca, bukan sesudah. Membaca 20 MB jadi string
        // lebih dulu lalu memotongnya tetap membekukan tab — pemotongannya
        // harus terjadi di sisi blob.
        const cut  = blob.size > PREVIEW_MAX_BYTES;
        const body = cut ? blob.slice(0, PREVIEW_MAX_BYTES) : blob;
        const s    = await body.text();
        if (!alive) return;
        setText(s); setTrunc(cut);
      } catch (e) {
        // Galat dari endpoint blob datang sebagai Blob, bukan JSON — tanpa
        // pembacaan ini pesannya selalu tampil sebagai "[object Blob]".
        // Pola yang sama dipakai `download()` di bawah.
        let m = e.message;
        try { if (e.response?.data instanceof Blob) m = JSON.parse(await e.response.data.text()).error || m; }
        catch (_) { /* biarkan pesan aslinya */ }
        if (alive) setErr(m);
      }
    })();
    return () => { alive = false; };
  }, [name]);

  // Esc menutup modal. Tanpa ini satu-satunya jalan keluar adalah tombol ✕,
  // yang berada di luar jangkauan saat berkasnya panjang dan sudah di-scroll.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard ditolak browser — diamkan, tombolnya tetap ada */ }
  }, [text]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}>
      {/* stopPropagation: klik DI DALAM modal tidak boleh menutupnya, kalau
          tidak menyeleksi teks untuk disalin akan membuat modal hilang. */}
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
          <FileText style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" />
          <span className="font-mono text-xs text-slate-700 truncate">{name}</span>
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {text !== null && (
              <button type="button" onClick={copy}
                className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-600">
                {copied
                  ? <><Check style={{ width: 12, height: 12 }} /> Copied</>
                  : <><Copy style={{ width: 12, height: 12 }} /> Copy</>}
              </button>
            )}
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X style={{ width: 15, height: 15 }} />
            </button>
          </div>
        </div>

        {trunc && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border-b border-amber-200 px-4 py-1.5">
            Preview truncated at {Math.round(PREVIEW_MAX_BYTES / 1024)} KB — download the file for the full contents.
          </div>
        )}

        <div className="overflow-auto p-4 flex-1">
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5">{err}</div>}
          {!err && text === null && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> {t('common.loading')}
            </div>
          )}
          {/* Teks polos, bukan HTML — lihat catatan di atas komponen ini. */}
          {!err && text !== null && (
            <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
              {text}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function DraftsPanel() {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr]   = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);   // nama berkas yang sedang dipratinjau

  const load = useCallback(() => {
    api.get('/api/ai/drafts')
      .then(({ data }) => setInfo(data))
      .catch(e => setErr(e.response?.data?.error || e.message));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const download = useCallback(async (name) => {
    setBusy(`dl:${name}`); setErr(null);
    let url = null;
    try {
      const res = await api.get(`/api/ai/drafts/file/${encodeURIComponent(name)}`,
        { responseType: 'blob' });
      url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // Galat dari endpoint blob datang sebagai Blob, bukan JSON — tanpa
      // pembacaan ini pesannya selalu tampil sebagai "[object Blob]".
      let m = e.message;
      try { if (e.response?.data instanceof Blob) m = JSON.parse(await e.response.data.text()).error || m; }
      catch (_) { /* biarkan pesan aslinya */ }
      setErr(m);
    } finally {
      // Dibebaskan tertunda: mencabut URL terlalu cepat membatalkan unduhan yang
      // baru dimulai di sebagian browser.
      if (url) setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      setBusy('');
    }
  }, []);

  const del = useCallback(async (name) => {
    setBusy(`rm:${name}`); setErr(null);
    try {
      await api.delete(`/api/ai/drafts/${encodeURIComponent(name)}`);
      load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  }, [load]);

  const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
  const when = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch (_) { return ''; }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600 flex-shrink-0">
        <FolderDown style={{ width: 13, height: 13 }} />
        {t('aiCoding.dft.open')}
      </button>
    );
  }

  return (
    <div className="w-full border-t border-slate-100 pt-2 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <FolderDown style={{ width: 13, height: 13 }} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-600">{t('aiCoding.dft.title')}</span>
        <button type="button" onClick={load}
          className="text-xs text-brand-600 hover:text-brand-800">{t('aiCoding.dft.refresh')}</button>
        <button type="button" onClick={() => setOpen(false)}
          className="text-xs text-slate-400 hover:text-slate-600 ml-auto">
          {t('aiCoding.sbx.close')}
        </button>
      </div>

      {info && <div className="text-[11px] text-slate-400 mt-1.5 font-mono truncate">{info.root}</div>}
      {/* Direktori belum ada = write_file belum pernah dipakai, ATAU rootnya
          berbeda dari yang dipakai ai-service. Dua sebab, satu gejala — jadi
          keduanya disebut. */}
      {info && !info.exists && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200
                        rounded-md px-2 py-1.5 mt-2">{t('aiCoding.dft.missing')}</div>
      )}
      {err && <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200
                              rounded-md px-2 py-1.5 mt-2">{err}</div>}

      {info?.drafts?.length > 0 && (
        <div className="mt-2 space-y-1">
          {info.drafts.map(d => (
            <div key={d.name} className="flex items-center gap-2 text-xs bg-white border border-slate-200
                                         rounded-lg px-2 py-1">
              <FileText style={{ width: 12, height: 12 }} className="text-slate-400 flex-shrink-0" />
              {/* Nama berkas jadi tombol pratinjau. Berkas yang tidak boleh
                  diunduh juga tidak bisa dipratinjau — endpoint yang dipakai
                  sama, jadi `downloadable` adalah penjaga yang tepat. Dibiarkan
                  sebagai teks biasa supaya tidak menjanjikan aksi yang akan
                  gagal. */}
              {d.downloadable ? (
                <button type="button" onClick={() => setPreview(d.name)}
                  title="Preview"
                  className="font-mono text-slate-700 truncate hover:text-brand-600 hover:underline text-left">
                  {d.name}
                </button>
              ) : (
                <span className="font-mono text-slate-700 truncate">{d.name}</span>
              )}
              <span className="text-slate-400 flex-shrink-0">{kb(d.bytes)}</span>
              <span className="text-slate-300 flex-shrink-0 hidden sm:inline">{when(d.updated_at)}</span>
              <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                {d.downloadable ? (
                  <>
                    <button type="button" onClick={() => setPreview(d.name)}
                      title="Preview"
                      className="text-slate-400 hover:text-brand-600">
                      <Eye style={{ width: 12, height: 12 }} />
                    </button>
                    <button type="button" disabled={busy === `dl:${d.name}`} onClick={() => download(d.name)}
                      title={t('aiCoding.dft.download')}
                      className="text-slate-400 hover:text-brand-600 disabled:opacity-50">
                      {busy === `dl:${d.name}`
                        ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                        : <Download style={{ width: 12, height: 12 }} />}
                    </button>
                  </>
                ) : (
                  <span title={t('aiCoding.dft.blocked')} className="text-slate-300">
                    <AlertTriangle style={{ width: 12, height: 12 }} />
                  </span>
                )}
                <button type="button" disabled={busy === `rm:${d.name}`} onClick={() => del(d.name)}
                  title={t('aiCoding.dft.delete')}
                  className="text-slate-400 hover:text-rose-600 disabled:opacity-50">
                  {busy === `rm:${d.name}`
                    ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                    : <Trash2 style={{ width: 12, height: 12 }} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {info?.exists && !info.drafts?.length && (
        <div className="text-[11px] text-slate-400 mt-2">{t('aiCoding.dft.empty')}</div>
      )}

      {preview && <DraftPreviewModal name={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// Panel usulan perubahan kode dari AI. Polanya sengaja meniru DraftsPanel:
// tombol ringkas selagi tertutup, blok w-full begitu dibuka. Toolbar di atas
// memakai flex-wrap, jadi w-full cukup untuk membuatnya turun ke barisnya
// sendiri tanpa mengubah tata letak apa pun di sekitarnya.
function ChangesPanel() {
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const lastPendingRef = useRef(null);
  // Poll jumlah usulan pending tiap 15 detik; panel dibuka otomatis bila jumlahnya NAIK
  // (mis. /agent baru saja membuat usulan), supaya user tidak perlu mencarinya.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const t = localStorage.getItem('auth_token');
        const r = await fetch('/api/ai/changes?status=pending', { headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (!r.ok) return;
        const b = await r.json();
        const n = Array.isArray(b && b.items) ? b.items.length : 0;
        if (!alive) return;
        if (lastPendingRef.current !== null && n > lastPendingRef.current) setOpen(true);
        lastPendingRef.current = n;
        setPendingCount(n);
      } catch (_) { /* abaikan: polling berikutnya mencoba lagi */ }
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-700">
        Usulan Perubahan
        {pendingCount > 0 && (
          <span className="ml-0.5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">{pendingCount}</span>
        )}
      </button>
    );
  }
  return (
    <div className="w-full border-t border-slate-100 pt-2 mt-2">
      <AIChangeReview onClose={() => setOpen(false)} />
    </div>
  );
}

export default function AICoding() {
  const { t, lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [rootsKey, setRootsKey] = useState(0);
  const [hasSandboxRoot, setHasSandboxRoot] = useState(false);

  // ── [RESUME] Dua sumber sesi lanjutan ──────────────────────────────────────
  // 1. Router state — tombol "Continue this session" di halaman AI Chat History
  // 2. Sidebar riwayat di kiri halaman ini
  //
  // Dulu ini `useState(() => location.state?.resume)` tanpa setter, karena hanya
  // ada satu sumber. Sekarang harus bisa di-set: sidebar mengganti sesi TANPA
  // navigasi router, jadi tidak ada `location.state` baru yang bisa dibaca.
  //
  // Router state tetap dibersihkan setelah dibaca. Tanpa itu history.state ikut
  // bertahan: menekan Back lalu Forward, atau kembali ke halaman ini dari menu,
  // akan memuat ulang transkrip lama dan mengunci sessionId lama — padahal user
  // mungkin sudah memulai sesi baru dari sidebar.
  const [resume, setResume] = useState(() => location.state?.resume || null);
  useEffect(() => {
    if (location.state?.resume) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []); // eslint-disable-line

  // ── Sidebar riwayat ────────────────────────────────────────────────────────
  // `dashboard` di ai_chat_history menyimpan NAMA TAMPILAN, bukan key. Untuk
  // halaman ini nilainya selalu "Coding" (CODE_KEY tidak ada di DASH_HINTS,
  // sehingga getDashboardContext jatuh ke activeLabel). Tetap dihitung lewat
  // fungsi yang sama, bukan ditulis sebagai string literal, supaya filter tidak
  // bisa menyimpang dari apa yang benar-benar disimpan AIChat.
  const CODING_LABEL  = 'Coding';
  const dashboardName = getDashboardContext(CODE_KEY, CODING_LABEL)?.name || CODING_LABEL;

  // chatKey memaksa remount AIChat. Prop `resume` hanya dibaca di initializer
  // useState/useRef di dalam AIChat — mengganti propnya saja tidak memuat apa
  // pun. Nilai awal mengikuti pola lama supaya perilaku "Continue" dari halaman
  // AI Chat History tidak berubah.
  const [chatKey, setChatKey] = useState(
    () => (location.state?.resume?.session_id
      ? `ai-coding-${location.state.resume.session_id}`
      : 'ai-coding')
  );
  const [activeSessionId, setActiveSessionId] = useState(
    () => location.state?.resume?.session_id || null
  );
  const [historyTick, setHistoryTick] = useState(0);
  const [picking, setPicking]         = useState(false);
  // Sidebar bisa disembunyikan: halaman ini penuh dengan keluaran kode yang
  // lebar, dan 260px bukan biaya yang selalu pantas dibayar. Default TERTUTUP,
  // dan saling meniadakan dengan sidebar navigasi — lihat useHistorySidebarSync.js.
  const [showHistory, toggleHistory] = useHistorySidebarSync('ai_coding');
  // Mobile: sidebar riwayat jadi drawer overlay, bukan kolom yang menghimpit chat.
  const isMobile = useIsMobile();

  const handleSessionActivity = useCallback((sid) => {
    setActiveSessionId(sid);
    setHistoryTick(n => n + 1);
  }, []);

  const handlePickSession = useCallback(async (sessionId) => {
    if (picking || sessionId === activeSessionId) return;
    setPicking(true);
    try {
      const r = await api.get(`/api/ai/chat-history/session/${encodeURIComponent(sessionId)}`);
      setResume({ session_id: sessionId, messages: toChatMessages(r.data, lang) });
      setChatKey(`ai-coding-${sessionId}`);
      setActiveSessionId(sessionId);
    } catch (e) {
      console.error('[AICoding] gagal memuat sesi:',
        e?.response?.status || '', e?.response?.data?.error || e.message);
      alert(`Gagal memuat sesi: ${e?.response?.data?.error || e.message}`);
    } finally {
      setPicking(false);
    }
  }, [picking, activeSessionId, lang]);

  const handleNewChat = useCallback(() => {
    setResume(null);
    // Key unik, bukan konstanta 'ai-coding': kalau nilainya kebetulan sudah sama,
    // React tidak me-remount dan transkrip lama tetap di layar.
    setChatKey(`ai-coding-new-${Date.now()}`);
    setActiveSessionId(null);
  }, []);

  // ── [DEEP LINK] ?session=<id> — sumber sesi lanjutan yang KETIGA ────────────
  // Dipakai "Open in new tab" dari sidebar riwayat. Beda dari router state:
  // nilainya bertahan di URL, jadi tab hasil bisa di-refresh dan di-bookmark.
  //
  // Param sengaja TIDAK dihapus setelah dipakai — itu justru inti gunanya.
  // Konsekuensi yang diterima: kalau di tab itu user pindah sesi lewat sidebar
  // lalu me-refresh, ia kembali ke sesi yang ada di URL. Menyinkronkan URL tiap
  // ganti sesi ditolak karena akan bertabrakan dengan `?src` di AIWorkspace.
  //
  // Guard pakai ref-nilai-terakhir, bukan "sekali lalu selesai": halaman AI
  // tidak selalu unmount saat navigasi (DashboardContainer menyembunyikan lewat
  // CSS), sehingga guard sekali-jalan akan terkunci selamanya.
  const lastDeepSidRef = useRef(null);
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get('session') || '';
    if (!sid || sid === lastDeepSidRef.current) return;
    lastDeepSidRef.current = sid;
    handlePickSession(sid);
  }, [location.search, handlePickSession]);

  useEffect(() => {
    loadConfigFromServer()
      .then(cfg => setConfig(cfg || getDefaultConfig()))
      .catch(() => setConfig(getDefaultConfig()))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="flex flex-col h-[calc(100vh-4rem)]"
      style={isMobile ? { height: 'calc(100dvh - 4rem - 56px - env(safe-area-inset-bottom))' } : undefined}
    >
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={toggleHistory}
            title={showHistory ? 'Sembunyikan riwayat' : 'Tampilkan riwayat'}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70 hover:text-slate-700 transition-colors flex-shrink-0"
          >
            {showHistory
              ? <PanelLeftClose style={{ width: 16, height: 16 }} />
              : <PanelLeftOpen  style={{ width: 16, height: 16 }} />}
          </button>
          <RootPicker reloadKey={rootsKey}
            onRoots={rs => setHasSandboxRoot((rs || []).some(r => /sandbox/i.test(r)))} />
          {/* Panel hanya berguna bila ai-service memang punya root sandbox di
              AI_TOOLS_ROOTS. Tanpa itu, unggahan berhasil tapi hasilnya tidak
              akan pernah muncul di dropdown — lebih baik tombolnya tidak ada
              daripada menuntun ke jalan buntu. */}
          {hasSandboxRoot && <SandboxPanel onChanged={() => setRootsKey(k => k + 1)} />}
          {/* Panel draft TIDAK digerbangi hasSandboxRoot: ia membaca disk lewat
              backend dashboard, bukan lewat AI_TOOLS_ROOTS. Draft tetap perlu
              diambil walau root sandbox tidak dikonfigurasi sama sekali. */}
          <DraftsPanel />
          <ChangesPanel />
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-row">
        {showHistory && isMobile && (
          <div
            className="fixed inset-0 z-[780] bg-slate-900/40"
            onClick={toggleHistory}
            aria-hidden="true"
          />
        )}
        {showHistory && (
          <div
            className={isMobile ? 'fixed left-0 top-0 bottom-0 z-[790] bg-white shadow-2xl min-h-0' : 'flex-shrink-0 h-full min-h-0'}
            style={isMobile ? { width: 'min(85vw, 320px)' } : { width: 260 }}
          >
            <AIHistorySidebar
              dashboardName={dashboardName}
              activeSessionId={activeSessionId}
              refreshToken={historyTick}
              onPick={(...a) => { if (isMobile) toggleHistory(); return handlePickSession(...a); }}
              onNewChat={(...a) => { if (isMobile) toggleHistory(); return handleNewChat(...a); }}
              sessionHref={sid => `/dashboard/ai-coding?session=${encodeURIComponent(sid)}`}
              lang={lang}
            />
          </div>
        )}
        <div className="flex-1 min-w-0 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full text-sm text-slate-400">
              {t('common.loading')}
            </div>
          ) : (
            <AIChat
              // key ikut session_id: kalau tidak, React memakai ulang instance
              // AIChat yang sudah ada dan seluruh initializer [RESUME] tidak
              // pernah dijalankan — transkrip lama tidak muncul, sessionId lama
              // tidak terpakai, dan gejalanya "tombol Continue tidak berfungsi".
              // Kini dikelola sebagai state (chatKey) karena sidebar juga bisa
              // mengganti sesi tanpa navigasi router.
              key={chatKey}
              currentPath={location.pathname}
              activeKey={CODE_KEY}
              activeLabel={CODING_LABEL}
              initialConfig={config}
              onConfigChange={setConfig}
              resume={resume}
              onSessionActivity={handleSessionActivity}
              onClose={() => navigate('/dashboard')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Engine selector (Agent SDK) ─────────────────────────────────────────────
// Stored per browser. AIChat reads 'aiCoding.engine' at send time and prefixes
// the question with "/agent @<profile>" — the backend does the actual routing.
const AGENT_ENGINE_KEY = 'aiCoding.engine';
const AGENT_ENGINE_OPTIONS = [
  { value: '', label: 'Built-in' },
  { value: 'default', label: 'Agent SDK (default)' },
  { value: 'bedrock', label: 'Agent · Bedrock' },
  { value: 'sylor', label: 'Agent · Sylor' },
  { value: 'mind', label: 'Agent · Mind' },
];
function EngineSelect() {
  const [v, setV] = useState(() => { try { return localStorage.getItem(AGENT_ENGINE_KEY) || ''; } catch (_) { return ''; } });
  const change = (x) => { setV(x); try { localStorage.setItem(AGENT_ENGINE_KEY, x); } catch (_) {} try { window.dispatchEvent(new Event('aiCoding.engine')); } catch (_) {} };
  return (
    <>
      <span className="text-xs text-slate-500 flex-shrink-0 ml-2">Engine</span>
      <select
        value={v}
        onChange={e => change(e.target.value)}
        title="Built-in = existing Coding engine. Agent = Claude Agent SDK (same as typing /agent @profile)."
        className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white outline-none focus:border-brand-400"
      >
        {AGENT_ENGINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </>
  );
}
